import styled from '@emotion/styled';
import {Container} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {PagesTransition} from 'UI/animations/layout';
import {Footer} from 'UI/modules/PageLayout/Footer';
import {Header} from 'UI/modules/PageLayout/Header';
import {MetaData} from 'UI/modules/PageLayout/MetaData';
import {Tracking} from 'UI/modules/PageLayout/Tracking';

import type {PageData} from 'PayloadUI/dataSelectors/getPageData';
import type {WithChildren} from 'types/global';
import type {Footer as FooterType, Header as HeaderType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the props expected by the `PageLayout` component.
 * It specifies the required layout data such as header and footer content, as well as routing and metadata for page transitions.
 * Optional properties like `testId` and `title` allow for enhanced testing and browser tab labeling.
 * This interface ensures that the layout is flexible, testable, and semantically complete.
 */
interface ComponentProps {
    /**
     * The data required to render the footer component, typically structured content like links or contact info.
     */
    footerData: FooterType;
    /**
     * The data required to render the header component, such as navigation or branding elements.
     */
    headerData: HeaderType;
    /**
     * Indicates if the current page is the homepage, which may affect layout or metadata rendering.
     */
    isHomepage?: boolean | null;
    /**
     * Combined metadata used for SEO, structured data, and canonical URL generation.
     */
    pageMetaData: PageData['pageMetaData'];
    /**
     * The route used to differentiate pages for animation transitions. Passed as the React `key` to re-trigger animations.
     */
    route: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'PageLayout'
     */
    testId?: string;
}

/**
 * A layout component that structures the overall page with header, main content, and footer.
 * The `PageLayout` component provides a consistent scaffold for all pages, including
 * metadata handling, animated page transitions, and reusable header/footer sections.
 * It accepts children for the main page content and applies transitions using
 * Framer Motion’s `AnimatePresence`.
 * The header and footer receive their respective data props for rendering navigation,
 * branding, and other contextual information. The `MetaData` component ensures that
 * SEO and page metadata are correctly applied based on `pageMetaData`.
 * A `testId` is used as a prefix for targeting header and footer in end-to-end tests,
 * ensuring reliable test automation.
 *
 * @param props              The component props.
 * @param props.children     The main page content to be rendered between the header and footer.
 * @param props.footerData   Data configuration for the `Footer` component.
 * @param props.headerData   Data configuration for the `Header` component.
 * @param props.isHomepage   A flag indicating whether the layout is used on the homepage, potentially affecting header rendering.
 * @param props.pageMetaData Metadata configuration for the page, passed to the `MetaData` component.
 * @param props.route        The current route, used as a key to trigger animated transitions.
 * @param props.testId       An optional identifier used for testing, applied as a prefix for header/footer test IDs. Defaults to `'PageLayout'`.
 * @returns A JSX element rendering the page structure with metadata, header, content, and footer.
 *
 * @example
 * ```tsx
 * <PageLayout
 *   headerData={headerConfig}
 *   footerData={footerConfig}
 *   pageMetaData={metaData}
 *   route="/about"
 *   isHomepage={false}
 * >
 *   <AboutPageContent />
 * </PageLayout>
 * ```
 */
const PageLayout = ({
    children,
    footerData,
    headerData,
    isHomepage,
    pageMetaData,
    route,
    testId = 'PageLayout'
}: WithChildren<ComponentProps>) => (
    <>
        <Tracking />
        <MetaData pageMetaData={pageMetaData} />
        <Header headerData={headerData} isHomepage={isHomepage} testId={`${testId}-Header`} />
        <AnimatePresence mode="wait">
            <Content
                key={route}
                animate="enter"
                as="main"
                exit="exit"
                initial="initial"
                variants={PagesTransition}
                hasNoPadding
                isFluid
            >
                {children}
            </Content>
        </AnimatePresence>
        <Footer footerData={footerData} testId={`${testId}-Footer`} />
    </>
);

PageLayout.displayName = 'PageLayout';

export {PageLayout};

const Content = styled(motion.create(Container))`
    flex: 1 2 auto;
`;